import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketImageUrl, marketTitle, searchMarket } from '../../lib/marketCatalog';
import { marketCategoryLabel } from '../../lib/marketCategories';
import { adminCollectionDetail, adminCollections, adminDeleteCollection, adminPinProduct, adminRefreshCollections, adminSaveCollection, RULES } from '../../lib/marketAdminCollections';

// Home-page collections (staff). Automatic collections are ranked on real data
// (the rule and its numbers are shown for every product); pins always lead;
// manual collections are exactly the pinned list. The home page shows each
// product once, in collection order, and only collections with enough items.
// Nothing here is random: the ranking changes on refresh (daily, or the button).

const fmtDate = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '');
// <input type="datetime-local"> works in LOCAL time; the database in UTC.
const toLocalInput = (v) => { if (!v) return ''; const d = new Date(v); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const toIso = (v) => (v ? new Date(v).toISOString() : null);

function Reason({ r, isAr }) {
  if (!r) return null;
  const bits = [];
  if (r.saving != null) bits.push(`${isAr ? 'وفر' : 'saves'} ${Math.round(r.saving * 100)}% @ ${r.best_tier_qty}`);
  if (r.service_score != null) bits.push(`${isAr ? 'تقييم' : 'score'} ${r.service_score}`);
  if (r.quality != null) bits.push(`${isAr ? 'جودة' : 'quality'} ${r.quality}`);
  if (r.paid_orders != null) bits.push(`${r.paid_orders} ${isAr ? 'طلب' : 'orders'}`);
  if (r.added) bits.push(fmtDate(r.added));
  if (r.moq != null) bits.push(`MOQ ${r.moq}`);
  return <span dir="ltr" style={{ fontSize: 11, color: '#777' }}>{bits.join(' · ')}</span>;
}

function ProductLine({ p, isAr, lang, right }) {
  const img = (Array.isArray(p?.images) ? p.images : [])[0]?.ref;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f1eee8' }}>
      {img ? <img src={marketImageUrl(img, true)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 36, height: 36, background: '#eee', borderRadius: 6 }} />}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{marketTitle(p, lang)}</div>
        <div style={{ fontSize: 11, color: '#888' }}>{marketCategoryLabel(p?.category, lang)}{p?.review_status === 'hidden' ? ` · ${isAr ? 'مخفي' : 'hidden'}` : ''}</div>
      </div>
      {right}
    </div>
  );
}

function CollectionCard({ col, isAr, lang, flash, onChanged }) {
  const [f, setF] = useState(col);
  const [open, setOpen] = useState(false);
  const [det, setDet] = useState(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  useEffect(() => { setF(col); }, [col]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const loadDet = useCallback(async () => setDet(await adminCollectionDetail(col.id)), [col.id]);
  useEffect(() => { if (open) loadDet(); }, [open, loadDet]);
  const save = async () => {
    const r = await adminSaveCollection({
      id: col.id, title_ar: f.title_ar, title_en: f.title_en, subtitle_ar: f.subtitle_ar || null, subtitle_en: f.subtitle_en || null,
      visible: !!f.visible, position: Number(f.position), max_items: Number(f.max_items), min_items: Number(f.min_items),
      per_category_cap: Number(f.per_category_cap), per_seller_cap: Number(f.per_seller_cap), params: f.params || {},
      starts_at: toIso(f.starts_at), ends_at: toIso(f.ends_at),
    });
    flash(r.ok ? (isAr ? 'حُفظت المجموعة وأُعيد ترتيبها' : 'Saved and re-ranked') : r.error);
    if (r.ok) { onChanged(); if (open) loadDet(); }
  };
  const pin = async (productId, position) => {
    const r = await adminPinProduct(col.id, productId, position);
    flash(r.ok ? (position == null ? (isAr ? 'أُلغي التثبيت' : 'Unpinned') : (isAr ? 'ثُبّت' : 'Pinned')) : r.error);
    if (r.ok) loadDet();
  };
  const rule = RULES[col.rule];
  const pinnedIds = new Set((det?.pins || []).map((x) => x.product?.id));
  return (
    <div className="ac-card" data-collection={col.slug} style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{isAr ? f.title_ar : f.title_en}</strong>
        <span className="ac-chip" style={{ cursor: 'default', fontSize: 11 }}>{col.mode === 'auto' ? (rule ? (isAr ? rule[0] : rule[1]) : col.rule) : (isAr ? 'يدوية' : 'Manual')}</span>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" data-visible={col.slug} checked={!!f.visible} onChange={(e) => set('visible', e.target.checked)} />{isAr ? 'ظاهرة' : 'Visible'}
        </label>
        <span style={{ fontSize: 11, color: '#888' }} dir="ltr">{col.refreshed_at ? `ranked ${fmtDate(col.refreshed_at)}` : ''}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6 }}>
        <input className="ac-input" placeholder="العنوان بالعربية" value={f.title_ar || ''} onChange={(e) => set('title_ar', e.target.value)} dir="rtl" />
        <input className="ac-input" placeholder="Title (English)" value={f.title_en || ''} onChange={(e) => set('title_en', e.target.value)} dir="ltr" />
        <input className="ac-input" placeholder="وصف قصير" value={f.subtitle_ar || ''} onChange={(e) => set('subtitle_ar', e.target.value)} dir="rtl" />
        <input className="ac-input" placeholder="Subtitle" value={f.subtitle_en || ''} onChange={(e) => set('subtitle_en', e.target.value)} dir="ltr" />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
        {[['position', isAr ? 'الترتيب' : 'Order'], ['max_items', isAr ? 'عدد المعروض' : 'Shows'], ['min_items', isAr ? 'الحد الأدنى' : 'Min'],
          ['per_category_cap', isAr ? 'لكل قسم' : 'Per category'], ['per_seller_cap', isAr ? 'لكل مورد' : 'Per supplier']].map(([k, l]) => (
          <label key={k} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>{l}<input className="ac-input" style={{ width: 64 }} type="number" value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} dir="ltr" /></label>
        ))}
        {col.mode === 'auto' && Object.keys(rule?.[2] || {}).map((k) => (
          <label key={k} style={{ display: 'flex', gap: 4, alignItems: 'center' }} dir="ltr">{k}
            <input className="ac-input" style={{ width: 72 }} type="number" step="any" value={f.params?.[k] ?? rule[2][k]} onChange={(e) => set('params', { ...f.params, [k]: Number(e.target.value) })} />
          </label>
        ))}
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>{isAr ? 'من' : 'From'}<input className="ac-input" type="datetime-local" value={toLocalInput(f.starts_at)} onChange={(e) => set('starts_at', e.target.value || null)} /></label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>{isAr ? 'إلى' : 'Until'}<input className="ac-input" type="datetime-local" value={toLocalInput(f.ends_at)} onChange={(e) => set('ends_at', e.target.value || null)} /></label>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="ac-btn ac-btn-sm ac-btn-primary" data-save-collection={col.slug} onClick={save}>{isAr ? 'حفظ' : 'Save'}</button>
        <button className="ac-btn ac-btn-sm" data-open-collection={col.slug} onClick={() => setOpen((x) => !x)}>{open ? (isAr ? 'إخفاء المنتجات' : 'Hide products') : (isAr ? 'المنتجات والتثبيت' : 'Products & pins')}</button>
        <a className="ac-btn ac-btn-sm" href={`/market/collection/${col.slug}`} target="_blank" rel="noreferrer">{isAr ? 'معاينة' : 'Preview'}</a>
        <button className="ac-btn ac-btn-sm" style={{ color: '#B3261E' }} onClick={async () => { if (!window.confirm(isAr ? 'حذف المجموعة؟ المنتجات لا تتأثر.' : 'Delete this collection? Products are not affected.')) return; const r = await adminDeleteCollection(col.id); flash(r.ok ? 'OK' : r.error); if (r.ok) onChanged(); }}>{isAr ? 'حذف' : 'Delete'}</button>
      </div>
      {open && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
          <div>
            <h4 style={{ margin: '4px 0' }}>{isAr ? 'المثبّتة (تظهر أولًا)' : 'Pinned (shown first)'}</h4>
            {(det?.pins || []).map((x) => (
              <ProductLine key={x.product.id} p={x.product} isAr={isAr} lang={lang} right={<>
                <input className="ac-input" style={{ width: 52 }} type="number" defaultValue={x.position} onBlur={(e) => Number(e.target.value) !== x.position && pin(x.product.id, Number(e.target.value))} dir="ltr" />
                <button className="ac-btn ac-btn-sm" onClick={() => pin(x.product.id, null)}>{isAr ? 'إلغاء' : 'Unpin'}</button>
              </>} />
            ))}
            <form style={{ display: 'flex', gap: 6, marginTop: 8 }} onSubmit={async (e) => { e.preventDefault(); setFound((await searchMarket({ q, limit: 8 })).rows); }}>
              <input className="ac-input" data-pin-search={col.slug} placeholder={isAr ? 'ابحث عن منتج لتثبيته' : 'Find a product to pin'} value={q} onChange={(e) => setQ(e.target.value)} />
              <button className="ac-btn ac-btn-sm" type="submit">{isAr ? 'بحث' : 'Search'}</button>
            </form>
            {found.filter((p) => !pinnedIds.has(p.id)).map((p) => (
              <ProductLine key={p.id} p={p} isAr={isAr} lang={lang} right={<button className="ac-btn ac-btn-sm" data-pin={p.id} onClick={() => pin(p.id, (det?.pins?.length || 0) + 1)}>{isAr ? 'تثبيت' : 'Pin'}</button>} />
            ))}
          </div>
          {col.mode === 'auto' && (
            <div>
              <h4 style={{ margin: '4px 0' }}>{isAr ? 'الترتيب الآلي وسببه' : 'Automatic ranking and why'}</h4>
              <div data-ranked={det?.ranked?.length ?? 0} style={{ maxHeight: 360, overflowY: 'auto' }}>
                {(det?.ranked || []).map((x) => (
                  <ProductLine key={x.product.id} p={x.product} isAr={isAr} lang={lang} right={<><Reason r={x.reason} isAr={isAr} /><span style={{ fontSize: 11, width: 24, textAlign: 'end' }}>{x.rank}</span></>} />
                ))}
                {det && det.ranked.length === 0 && <div className="ac-placeholder">{isAr ? 'لا توجد بيانات كافية لهذه المجموعة بعد — لن تظهر للعملاء.' : 'Not enough data for this collection yet — customers will not see it.'}</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConsoleMarketCollections({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [rows, setRows] = useState(null);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };
  const load = useCallback(async () => setRows((await adminCollections()).rows), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const slug = `manual-${Date.now().toString(36)}`;
    const r = await adminSaveCollection({ slug, mode: 'manual', title_ar: 'مجموعة جديدة', title_en: 'New collection', visible: false, position: 90 });
    flash(r.ok ? (isAr ? 'أُنشئت (مخفية حتى تضيف منتجات)' : 'Created (hidden until you add products)') : r.error);
    load();
  };
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-collections">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'مجموعات الصفحة الرئيسية' : 'Home collections'}</h1>
            <p className="ac-sub">{isAr
              ? 'المجموعات الآلية تُرتَّب من بيانات حقيقية (السبب ظاهر لكل منتج)، والمثبّت يظهر أولًا. لا توجد مجموعة «الأكثر مبيعًا» أو «الرائج في الصين»: البيانات المتاحة لا تثبت ذلك.'
              : 'Automatic collections are ranked on real data (the reason is shown for every product); pins lead. There is no "best-selling" or "trending in China" collection: the available data cannot support it.'}</p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="ac-btn ac-btn-sm" data-refresh-collections="1" onClick={async () => { const r = await adminRefreshCollections(); flash(r.ok ? (isAr ? 'أُعيد الترتيب' : 'Re-ranked') : r.error); load(); }}>{isAr ? 'إعادة الترتيب الآن' : 'Re-rank now'}</button>
            <button className="ac-btn ac-btn-sm ac-btn-primary" data-new-collection="1" onClick={create}>{isAr ? 'مجموعة يدوية جديدة' : 'New manual collection'}</button>
          </div>
        </div>
        {rows == null ? <div className="ac-skel" style={{ height: 90 }} /> : (
          <div data-collections={rows.length} style={{ display: 'grid', gap: 10 }}>
            {rows.map((c) => <CollectionCard key={c.id} col={c} isAr={isAr} lang={lang} flash={flash} onChanged={load} />)}
          </div>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}
